<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="product-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <div class="row wrap g-1">
            <AffectedShops :item="item" />
            <Actions placement="product" :query="{ product: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="!isNew && !isLocked"
                  :label="$t('delete')"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>
            <ButtonComponent
              :label="isNew ? $t('create') : $t('save')"
              theme="primary"
              type="submit"
              :disabled="($refs.form && !$refs.form.validator.passes) || isLocked"
              :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
              :promise="promises.mutate"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <ProductForm v-model="data" @submit="submit" ref="form">
        <template #banner>
          <Alert v-if="isLocked" theme="error">{{ $t('productLockedAlert') }}</Alert>
        </template>
        <template #footer>
          <div v-if="!isNew" class="col" style="margin-left: auto">
            <div class="product-mutate__last-updated">
              {{ $t('lastUpdated') }} {{ item.updatedAt | Moment('DD. MMM YYYY HH:mm') }}
            </div>
          </div>
        </template>
      </ProductForm>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'products',
          })
        "
        :resource="$tc('product')"
      />
    </div>
  </Loader>
</template>

<script>
import { Moment } from '@/filters'
import { mapi18n } from '@/i18n'
import ModelMixin from '@/mixins/Model'
import Model from '@/models/Product'

import { mapActions, mapMutations, mapState } from 'vuex'

import Actions from '@/components/Actions'
import Card from '@/components/Card'
import HeaderComponent from '@/components/Header'
import ProductForm from '@/components/Product/Form'
import RemoveComponent from '@/components/Remove'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { DropdownComponent } from 'vue-elder-dropdown'
import AffectedShops from '@/components/Product/AffectedShops.vue'

export default {
  mixins: [
    ModelMixin({
      model(data, options) {
        const { pricing, ...rest } = data

        let vat = this.$store.state.enums.Region.vat.default
        if (this.$path('vat', pricing) !== undefined) vat = this.$path('vat', pricing)

        return Model(
          {
            category: this.$route.query.category,
            pricing: {
              ...pricing,
              vat,
            },
            ...rest,
          },
          options,
        )
      },
      guard: true,
      transform: {
        products: v => (v && v.length ? v.map(e => e.id) : null),
      },
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
    }
  },
  watch: {
    id: {
      handler(id = '') {
        if (!id) return this.set({})
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Product', ['item', 'promises']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingProduct() {
        return this.$t('loading', { resource: this.$tc('product', 1) }) + '...'
      },
    }),
    isLocked() {
      return !!this.$path('item.metadata.isLocked')
    },
    isNew() {
      return !this.id
    },
    breadcrumbs() {
      return [{ title: this.$tc('product', 2), action: { name: 'products' } }, { title: this.$metaInfo.title }]
    },
  },
  provide() {
    return {
      getData: this.getData,
    }
  },
  methods: {
    ...mapActions('Product', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Product', ['set']),
    submit() {
      if (this.isLocked) return
      let data = this.getData({ diff: !this.isNew })

      if (this.isNew && this.$route.query.reference) {
        let [onModel, ref] = (this.$route.query.reference || '').split(':')
        data.reference = { onModel, ref }
      }

      if (this.isNew && !data.category) {
        data.category = this.data.category
      }

      this.mutate([
        { id: this.id, ...data },
        {
          strategy: (item, payload) => ({ ...item, ...this.data, updatedAt: payload.updatedAt }),
        },
      ]).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'product-edit',
          params: { id: data.id },
        })
      })
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createProduct') : this.item.name || this.i18n.loadingProduct,
    }
  },
  filters: {
    Moment,
  },
  components: {
    HeaderComponent,
    DropdownComponent,
    RemoveComponent,
    Card,
    ProductForm,
    Actions,
    Alert,
    AffectedShops,
  },
}
</script>

<style lang="scss">
.product-mutate {
  background-color: $background-grey;

  &__last-updated {
    opacity: 0.7;
    margin-top: 0.5rem;
    font-size: 0.8em;
    letter-spacing: 0.25px;
  }

  .product-form .card__footer-content {
    align-items: start;
  }

  .alert {
    &__content > span {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }
  }
}
</style>
